import React, { useMemo } from 'react';
import {
  APIProvider,
  Map,
  AdvancedMarker,
  Pin,
  useMap,
  useMapsLibrary,
} from '@vis.gl/react-google-maps';

interface ITripWaypoint {
  lat: number;
  long: number;
  speed?: number;
  speedLimit?: number;
}

interface GoogleMapsTripProps {
  waypoints: ITripWaypoint[];
}

// Get API key from environment variable
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? '';

// Polyline component using the Maps JavaScript API directly
const TripPolyline: React.FC<{ waypoints: ITripWaypoint[] }> = ({
  waypoints,
}) => {
  const map = useMap();
  const geometryLibrary = useMapsLibrary('geometry');

  React.useEffect(() => {
    if (!map || !geometryLibrary || waypoints.length < 2) return;

    const path = waypoints.map((waypoint) => ({
      lat: waypoint.lat,
      lng: waypoint.long,
    }));

    const polyline = new window.google.maps.Polyline({
      path,
      strokeColor: '#2196F3',
      strokeOpacity: 0.8,
      strokeWeight: 4,
      geodesic: true,
    });

    polyline.setMap(map);

    // Fit bounds to show all waypoints
    const bounds = new window.google.maps.LatLngBounds();
    waypoints.forEach((waypoint) => {
      bounds.extend({ lat: waypoint.lat, lng: waypoint.long });
    });
    map.fitBounds(bounds);

    return () => {
      polyline.setMap(null);
    };
  }, [map, geometryLibrary, waypoints]);

  return null;
};

const GoogleMapsTrip: React.FC<GoogleMapsTripProps> = ({ waypoints }) => {
  // Calculate center of the route
  const center = useMemo(() => {
    if (waypoints.length === 0) {
      return { lat: 51.5074, lng: -0.1278 }; // Default to London
    }

    const sumLat = waypoints.reduce((sum, waypoint) => sum + waypoint.lat, 0);
    const sumLng = waypoints.reduce((sum, waypoint) => sum + waypoint.long, 0);

    return {
      lat: sumLat / waypoints.length,
      lng: sumLng / waypoints.length,
    };
  }, [waypoints]);

  if (waypoints.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
        }}
      >
        <i
          className="pi pi-map"
          style={{ fontSize: '3rem', color: '#999' }}
        ></i>
        <div style={{ marginTop: '16px', color: '#666' }}>
          No waypoint data available for this trip
        </div>
      </div>
    );
  }

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          padding: '20px',
        }}
      >
        <i
          className="pi pi-exclamation-triangle"
          style={{ fontSize: '3rem', color: '#ff9800' }}
        ></i>
        <h3 style={{ marginTop: '16px', marginBottom: '8px' }}>
          Google Maps API Key Required
        </h3>
        <p style={{ textAlign: 'center', color: '#666', maxWidth: '400px' }}>
          Please set the REACT_APP_GOOGLE_MAPS_API_KEY environment variable to
          display the map.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Google Map */}
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        <div style={{ height: '500px', width: '100%' }}>
          <Map
            defaultCenter={center}
            defaultZoom={13}
            mapId="trip-map"
            gestureHandling="greedy"
            disableDefaultUI={false}
            zoomControl={true}
            mapTypeControl={true}
            fullscreenControl={true}
            streetViewControl={false}
          >
            {/* Start point marker */}
            {waypoints.length > 0 && (
              <AdvancedMarker
                position={{ lat: waypoints[0].lat, lng: waypoints[0].long }}
              >
                <Pin
                  background="#4CAF50"
                  borderColor="#2E7D32"
                  glyphColor="white"
                />
              </AdvancedMarker>
            )}

            {/* End point marker */}
            {waypoints.length > 1 && (
              <AdvancedMarker
                position={{
                  lat: waypoints[waypoints.length - 1].lat,
                  lng: waypoints[waypoints.length - 1].long,
                }}
              >
                <Pin
                  background="#F44336"
                  borderColor="#C62828"
                  glyphColor="white"
                />
              </AdvancedMarker>
            )}

            {/* Route polyline */}
            <TripPolyline waypoints={waypoints} />
          </Map>
        </div>
      </APIProvider>

      {/* Map Legend */}
      <div
        style={{
          marginTop: '12px',
          display: 'flex',
          gap: '24px',
          fontSize: '14px',
          color: '#666',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <i
            className="pi pi-circle-fill"
            style={{ color: '#4CAF50', fontSize: '10px' }}
          ></i>
          Start Point
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <i
            className="pi pi-circle-fill"
            style={{ color: '#f44336', fontSize: '10px' }}
          ></i>
          End Point
        </div>
        <div style={{ marginLeft: 'auto', fontSize: '12px', color: '#999' }}>
          {waypoints.length} waypoints
        </div>
      </div>
    </div>
  );
};

export default GoogleMapsTrip;
